import { Box, CheckboxInput, SubscriberStarIcon } from '@deckee/deck-hand';
import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import LayerThumbnail from './LayerThumbnail';

interface LayerCardProps {
  title: string;
  subtitle: string;
  selected: boolean;
  onClick: () => void;
  imageUrl?: string;
  subscriberOnly?: boolean;
  /**
   * One-of-many (base maps) rather than independently toggled (overlays).
   * Single-select marks the whole card with a ring; the rest carry a
   * checkbox and never change the card's own appearance.
   */
  singleSelect?: boolean;
  /** Identifies the checkbox on multi-select cards. */
  name?: string;
}

/**
 * Box cannot express the control reset or the inset ring. The card surface is
 * the same colour throughout — only the ring (single-select) or the checkbox
 * (multi-select) reflects state, so there is no hover or press background.
 */
const cardSurface = css<{ $ringed: boolean }>`
  appearance: none;
  cursor: pointer;
  text-align: left;
  box-shadow: inset 0 0 0
    ${({ theme, $ringed }) =>
      $ringed ? `2px ${theme.colors.primary}` : `1px ${theme.colors.line}`};
`;

/** Base maps pick one of several, so the card itself is the control. */
const CardRadio = styled(Box).attrs({ as: 'button', type: 'button' })<{
  $ringed: boolean;
}>`
  ${cardSurface}
`;

/** Overlays own a checkbox, so the card is its label. */
const CardLabel = styled(Box).attrs({ as: 'label' })<{ $ringed: boolean }>`
  ${cardSurface}
`;

const layout = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  p: 2.5,
  border: '0',
  borderRadius: '12px',
  bg: 'white',
  gap: 2.5,
} as const;

/** A base map or overlay row: preview, copy, and its selection control. */
const LayerCard: FC<LayerCardProps> = ({
  title,
  subtitle,
  selected,
  onClick,
  imageUrl = undefined,
  subscriberOnly = false,
  singleSelect = false,
  name = undefined,
}) => {
  const content = (
    <>
      <LayerThumbnail imageUrl={imageUrl} />

      <Box display="flex" flexDirection="column" gap="1" flex="1 1 auto">
        <Box display="flex" alignItems="center" gap="2">
          <Box fontWeight="bold" fontSize="15px" color="black">
            {title}
          </Box>
          {subscriberOnly && (
            <Box display="flex" flex="0 0 auto">
              <SubscriberStarIcon size={18} />
            </Box>
          )}
        </Box>
        <Box fontSize="13px" color="darkergrey">
          {subtitle}
        </Box>
      </Box>
    </>
  );

  if (singleSelect) {
    return (
      <CardRadio
        {...layout}
        $ringed={selected}
        onClick={onClick}
        role="radio"
        aria-checked={selected}
      >
        {content}
      </CardRadio>
    );
  }

  return (
    <CardLabel {...layout} $ringed={false}>
      {content}
      <CheckboxInput name={name} checked={selected} onChange={onClick} />
    </CardLabel>
  );
};

export default LayerCard;
